<template>
  <div>
    <Card cardClasses="shadow-sm border-0">
      <!-- Header Section -->
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
        <div>
          <button class="btn btn-outline-secondary btn-sm me-2" @click="$router.go(-1)">
            <i class="bi bi-arrow-left me-1"></i> Back
          </button>
          <span class="h4 text-primary m-0 align-middle">
            <i class="bi bi-shop me-2"></i>Store Details
          </span>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <button
            v-if="canManageStore"
            class="btn btn-outline-primary"
            @click="openEditModal"
          >
            <i class="bi bi-pencil me-1"></i> Edit Store
          </button>
          <button
            v-if="!store.online && canManageStore"
            class="btn btn-outline-success"
            @click="openOnlineModal('add')"
          >
            <i class="bi bi-globe me-1"></i> Go Online
          </button>
          <button
            v-if="store.online && canManageStore"
            class="btn btn-outline-info"
            @click="openOnlineModal('edit')"
          >
            <i class="bi bi-pencil me-1"></i> Edit Online Details
          </button>
        </div>
      </div>

      <div class="container-fluid p-0">
        <!-- Loading Spinner -->
        <div v-if="loading" class="text-center py-5">
          <Spinner spinner-class="mb-1" />
          <p class="text-muted mt-2">Loading store details...</p>
        </div>

        <!-- Store Content -->
        <div v-else class="store-details-content fade-in">
          <!-- Store Header Overview -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-primary text-white py-3">
              <h5 class="mb-0"><i class="bi bi-shop me-2"></i>Store Overview</h5>
            </div>
            <div class="card-body">
              <div class="row align-items-center">
                <!-- Avatar Column -->
                <div class="col-md-3 text-center mb-3 mb-md-0">
                  <div class="avatar-container">
                    <div class="avatar-wrapper">
                      <div class="store-avatar bg-primary-light">
                        <span class="avatar-text">{{ store.name ? store.name.charAt(0).toUpperCase() : "" }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Details Column -->
                <div class="col-md-9">
                  <h2 class="store-title mb-2">{{ store.name }}</h2>

                  <div class="d-flex flex-wrap gap-2 mb-3">
                    <Badge pill :type="store.online ? 'success' : 'danger'">
                      <i class="bi" :class="store.online ? 'bi-check-circle' : 'bi-x-circle'"></i>
                      {{ store.online ? "Online" : "Offline" }}
                    </Badge>
                  </div>

                  <div class="d-flex flex-wrap align-items-center gap-4">
                    <div>
                      <span class="text-muted small d-block">Code</span>
                      <span class="fw-bold">{{ store.code || "-" }}</span>
                    </div>
                    <div>
                      <span class="text-muted small d-block">Phone</span>
                      <span class="fw-bold">{{ store.phone || "-" }}</span>
                    </div>
                    <div>
                      <span class="text-muted small d-block">Address</span>
                      <span class="fw-bold">{{ store.address || "-" }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Basic Information Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3">
              <h5 class="mb-0"><i class="bi bi-info-circle me-2"></i>Basic Information</h5>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6 col-lg-3">
                  <div class="info-group">
                    <h6 class="info-label text-muted small mb-1">Code</h6>
                    <p class="info-value fw-semibold mb-0">{{ store.code || "-" }}</p>
                  </div>
                </div>
                <div class="col-md-6 col-lg-3">
                  <div class="info-group">
                    <h6 class="info-label text-muted small mb-1">Name</h6>
                    <p class="info-value fw-semibold mb-0">{{ store.name || "-" }}</p>
                  </div>
                </div>
                <div class="col-md-6 col-lg-3">
                  <div class="info-group">
                    <h6 class="info-label text-muted small mb-1">Address</h6>
                    <p class="info-value fw-semibold mb-0">{{ store.address || "No address available" }}</p>
                  </div>
                </div>
                <div class="col-md-6 col-lg-3">
                  <div class="info-group">
                    <h6 class="info-label text-muted small mb-1">Phone</h6>
                    <p class="info-value fw-semibold mb-0">{{ store.phone || "No phone available" }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Online Information Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3 d-flex justify-content-between align-items-center">
              <h5 class="mb-0"><i class="bi bi-globe me-2"></i>Online Information</h5>
              <button
                v-if="canManageStore"
                class="btn btn-sm btn-outline-primary"
                @click="openOnlineModal(store.online ? 'edit' : 'add')"
              >
                <i class="bi bi-pencil me-1"></i> {{ store.online ? "Edit Online Details" : "Configure Online" }}
              </button>
            </div>
            <div class="card-body">
              <div class="row g-3">
                <div class="col-md-6 col-lg-3">
                  <div class="info-group">
                    <h6 class="info-label text-muted small mb-1">Status</h6>
                    <p class="info-value mb-0">
                      <span class="badge" :class="store.online ? 'bg-success' : 'bg-danger'">
                        <i class="bi" :class="store.online ? 'bi-check-circle' : 'bi-x-circle'"></i>
                        {{ store.online ? "Online" : "Offline" }}
                      </span>
                    </p>
                  </div>
                </div>
                <div class="col-md-6 col-lg-3">
                  <div class="info-group">
                    <h6 class="info-label text-muted small mb-1">Online Name</h6>
                    <p class="info-value fw-semibold mb-0">{{ store.online_name || "Not set" }}</p>
                  </div>
                </div>
                <div class="col-md-6 col-lg-6">
                  <div class="info-group">
                    <h6 class="info-label text-muted small mb-1">Catchphrase</h6>
                    <p class="info-value fw-semibold mb-0">{{ store.catchphrase || "Not set" }}</p>
                  </div>
                </div>
                <div class="col-md-12">
                  <div class="info-group">
                    <h6 class="info-label text-muted small mb-2">Background Banner</h6>
                    <div class="bg-light p-2 rounded border">
                      <img
                        :src="backgroundImage(store.background_image)"
                        class="img-fluid rounded"
                        alt="Background Banner"
                        style="max-height: 180px; width: 100%; object-fit: cover;"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Operating Hours Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3 d-flex justify-content-between align-items-center">
              <h5 class="mb-0"><i class="bi bi-clock me-2"></i>Operating Hours</h5>
              <button
                v-if="canManageStore"
                class="btn btn-sm btn-primary"
                @click="openAddOperatingHoursModal"
              >
                <i class="bi bi-plus-circle me-1"></i> {{ operatingHours.length === 0 ? "Add Operating Hours" : "Set All Hours" }}
              </button>
            </div>
            <div class="card-body">
              <div v-if="operatingHours.length === 0" class="text-center py-4 text-muted">
                <i class="bi bi-clock-history fs-3 d-block mb-2 opacity-50"></i>
                <p class="mb-2">No operating hours set for this store.</p>
                <button
                  v-if="canManageStore"
                  class="btn btn-outline-primary btn-sm"
                  @click="openAddOperatingHoursModal"
                >
                  <i class="bi bi-plus-circle me-1"></i> Add Operating Hours
                </button>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Day</th>
                      <th>Opening Time</th>
                      <th>Closing Time</th>
                      <th>Status</th>
                      <th v-if="canManageStore" class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="hour in operatingHours" :key="hour.id">
                      <td class="text-capitalize fw-semibold">{{ hour.day_of_week }}</td>
                      <td>{{ hour.is_closed ? "-" : hour.opening_time }}</td>
                      <td>{{ hour.is_closed ? "-" : hour.closing_time }}</td>
                      <td>
                        <span class="badge" :class="hour.is_closed ? 'bg-danger' : 'bg-success'">
                          {{ hour.is_closed ? "Closed" : "Open" }}
                        </span>
                      </td>
                      <td v-if="canManageStore" class="text-end">
                        <button class="btn btn-outline-primary btn-sm" @click="openEditOperatingHourModal(hour)" title="Edit">
                          <i class="bi bi-pencil"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Delivery Prices Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-3 d-flex justify-content-between align-items-center">
              <h5 class="mb-0"><i class="bi bi-truck me-2"></i>Delivery Prices</h5>
              <button
                v-if="canManageStore"
                class="btn btn-sm btn-primary"
                @click="openDeliveryPriceModal('add')"
              >
                <i class="bi bi-plus-circle me-1"></i> Add Delivery Price
              </button>
            </div>
            <div class="card-body">
              <div v-if="deliveryPrices.length === 0" class="text-center py-4 text-muted">
                <i class="bi bi-truck fs-3 d-block mb-2 opacity-50"></i>
                <p class="mb-2">No delivery prices set for this store.</p>
                <button
                  v-if="canManageStore"
                  class="btn btn-outline-primary btn-sm"
                  @click="openDeliveryPriceModal('add')"
                >
                  <i class="bi bi-plus-circle me-1"></i> Add Delivery Price
                </button>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Distance Range (km)</th>
                      <th>Price</th>
                      <th v-if="canManageStore" class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="price in deliveryPrices" :key="price.id">
                      <td>{{ price.from }} km - {{ price.to }} km</td>
                      <td class="fw-bold">{{ formatNumber(price.price) }}</td>
                      <td v-if="canManageStore" class="text-end">
                        <div class="btn-group btn-group-sm">
                          <button class="btn btn-outline-primary" @click="openDeliveryPriceModal('edit', price)" title="Edit">
                            <i class="bi bi-pencil"></i>
                          </button>
                          <button class="btn btn-outline-danger" @click="deleteDeliveryPrice(price.id)" title="Delete">
                            <i class="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Online Sale Notification Emails Section -->
          <OnlineSaleNotificationEmails v-if="store && store.id" :storeId="store.id" />
        </div>
      </div>
    </Card>

    <!-- Go Online / Edit Online Modal -->
    <Modal :open="onlineModal" @closed="onlineModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi bi-globe me-2 text-primary"></i>
          <span v-if="onlineModalMode === 'add'">Go Online - {{ store.name }}</span>
          <span v-else>Edit Online Details - {{ store.name }}</span>
        </h5>
        <button @click="onlineModal = false" type="button" class="btn-close outline-none" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveOnline">
          <div class="row g-3">
            <div v-if="onlineModalMode === 'edit'" class="col-md-12">
              <label for="onlineStatus" class="form-label fw-semibold">
                <i class="bi bi-shop-window me-1"></i>Online Store Status
              </label>
              <select id="onlineStatus" class="form-select" v-model.number="selectedStore.online">
                <option :value="1">Online</option>
                <option :value="0">Offline</option>
              </select>
              <small class="text-muted">Whether or not your store is active for online shopping</small>
            </div>

            <div class="col-md-12">
              <label for="online_name" class="form-label fw-semibold">
                <i class="bi bi-shop-window me-1"></i>Online Store Name <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                id="online_name"
                class="form-control"
                placeholder="Enter online store name"
                v-model="selectedStore.online_name"
                required
              />
              <small class="text-muted">This name will be displayed to your online customers</small>
            </div>

            <div class="col-md-12">
              <label for="catchphrase" class="form-label fw-semibold">
                <i class="bi bi-chat-quote me-1"></i>Catchphrase <span class="text-danger">*</span>
              </label>
              <input
                type="text"
                id="catchphrase"
                class="form-control"
                placeholder="Enter a catchy phrase for your store"
                v-model="selectedStore.catchphrase"
                required
              />
              <small class="text-muted">A short, memorable phrase that represents your store</small>
            </div>

            <div class="col-md-12">
              <label class="form-label fw-semibold">
                <i class="bi bi-image me-1"></i>Background Image
              </label>
              <div class="d-flex gap-2 mb-2">
                <button
                  type="button"
                  class="btn btn-outline-primary"
                  @click="$refs.refInputEl.click()"
                >
                  <i class="bi bi-upload me-1"></i> Upload Image
                </button>
                <input
                  ref="refInputEl"
                  type="file"
                  accept=".jpeg,.png,.jpg,.webp"
                  hidden
                  @change="displayImage"
                />
                <ImageCropper
                  :open="cropper.open"
                  :file="cropper.file"
                  :aspect-ratio="cropPreset.aspectRatio"
                  :output-width="cropPreset.width"
                  :output-height="cropPreset.height"
                  title="Crop store background"
                  hint="Used as the default banner in the online shop."
                  @cropped="onBackgroundCropped"
                  @closed="cropper.open = false"
                />
              </div>
              <small class="text-muted d-block mb-2">Select an image for your store banner (cropped to {{ cropPreset.label }}).</small>
              <div class="bg-light p-2 rounded border">
                <img
                  :src="selectedFile || backgroundImage(selectedStore.background_image)"
                  ref="backgroundImagePreview"
                  class="img-fluid rounded"
                  alt="Banner Preview"
                  style="max-height: 160px; width: 100%; object-fit: cover;"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="onlineModal = false">Cancel</button>
        <button type="button" class="btn btn-success" @click="saveOnline" :disabled="savingOnline">
          <span v-if="savingOnline" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-check-circle me-1"></i>
          {{ onlineModalMode === 'add' ? "Go Online" : "Save Online Details" }}
        </button>
      </div>
    </Modal>

    <!-- Edit Store Modal -->
    <Modal :open="storeModal" @closed="storeModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi bi-pencil-square me-2 text-primary"></i>Edit Store
        </h5>
        <button @click="storeModal = false" type="button" class="btn-close outline-none" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="updateStore">
          <div class="row g-3">
            <div class="col-md-6">
              <label for="store_code" class="form-label fw-semibold">Store Code</label>
              <input
                type="text"
                id="store_code"
                class="form-control"
                v-model="editStore.code"
                readonly
              />
              <small class="text-muted">Auto-generated code</small>
            </div>
            <div class="col-md-6">
              <label for="store_name" class="form-label fw-semibold">Branch Name <span class="text-danger">*</span></label>
              <input
                type="text"
                id="store_name"
                class="form-control"
                placeholder="Enter branch name"
                v-model="editStore.name"
                required
              />
            </div>
            <div class="col-md-6">
              <label for="store_address" class="form-label fw-semibold">Address</label>
              <input
                type="text"
                id="store_address"
                class="form-control"
                placeholder="Enter address"
                v-model="editStore.address"
              />
            </div>
            <div class="col-md-6">
              <label for="store_phone" class="form-label fw-semibold">Phone</label>
              <input
                type="text"
                id="store_phone"
                class="form-control"
                placeholder="Enter phone number"
                v-model="editStore.phone"
              />
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="storeModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" @click="updateStore" :disabled="savingStore">
          <span v-if="savingStore" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-check-circle me-1"></i> Update Store
        </button>
      </div>
    </Modal>

    <!-- Delivery Price Modal -->
    <Modal :open="deliveryPriceModal" @closed="deliveryPriceModal = false">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi bi-truck me-2 text-primary"></i>
          <span>{{ deliveryPriceModalMode === 'add' ? "Add Delivery Price" : "Edit Delivery Price" }}</span>
        </h5>
        <button @click="deliveryPriceModal = false" type="button" class="btn-close outline-none" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="saveDeliveryPrice">
          <div class="row g-3">
            <div class="col-md-6">
              <label for="priceFrom" class="form-label fw-semibold">From (km) <span class="text-danger">*</span></label>
              <input
                type="number"
                id="priceFrom"
                class="form-control"
                placeholder="0"
                v-model.number="selectedDeliveryPrice.from"
                min="0"
                step="0.1"
                required
              />
            </div>
            <div class="col-md-6">
              <label for="priceTo" class="form-label fw-semibold">To (km) <span class="text-danger">*</span></label>
              <input
                type="number"
                id="priceTo"
                class="form-control"
                placeholder="5"
                v-model.number="selectedDeliveryPrice.to"
                min="0"
                step="0.1"
                required
              />
            </div>
            <div class="col-md-12">
              <label for="priceAmount" class="form-label fw-semibold">Delivery Fee <span class="text-danger">*</span></label>
              <input
                type="number"
                id="priceAmount"
                class="form-control"
                placeholder="100"
                v-model.number="selectedDeliveryPrice.price"
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="deliveryPriceModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" @click="saveDeliveryPrice" :disabled="savingDeliveryPrice">
          <span v-if="savingDeliveryPrice" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-check-circle me-1"></i> Save Delivery Price
        </button>
      </div>
    </Modal>

    <!-- Add Operating Hours Modal (All Days Table) -->
    <Modal :open="addOperatingHoursModal" @closed="addOperatingHoursModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi bi-clock me-2 text-primary"></i>Set Operating Hours
        </h5>
        <button @click="addOperatingHoursModal = false" type="button" class="btn-close outline-none" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <div class="table-responsive">
          <table class="table table-sm table-bordered align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Day</th>
                <th>Opening Time</th>
                <th>Closing Time</th>
                <th class="text-center">Closed</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(hour, index) in newOperatingHours" :key="index">
                <td class="text-capitalize fw-semibold">{{ hour.day_of_week }}</td>
                <td>
                  <input
                    type="time"
                    class="form-control form-control-sm"
                    v-model="hour.opening_time"
                    :disabled="hour.is_closed"
                  />
                </td>
                <td>
                  <input
                    type="time"
                    class="form-control form-control-sm"
                    v-model="hour.closing_time"
                    :disabled="hour.is_closed"
                  />
                </td>
                <td class="text-center">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    v-model="hour.is_closed"
                    :id="'closedCheck-' + index"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="addOperatingHoursModal = false">Cancel</button>
        <button type="button" class="btn btn-success" @click="createOperatingHours" :disabled="savingOperatingHours">
          <span v-if="savingOperatingHours" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-check-circle me-1"></i> Save Operating Hours
        </button>
      </div>
    </Modal>

    <!-- Edit Single Operating Hour Modal -->
    <Modal :open="editOperatingHourModal" @closed="editOperatingHourModal = false">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0">
          <i class="bi bi-clock me-2 text-primary"></i>Edit Operating Hour ({{ selectedOperatingHour.day_of_week }})
        </h5>
        <button @click="editOperatingHourModal = false" type="button" class="btn-close outline-none" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <div class="mb-3">
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              v-model="selectedOperatingHour.is_closed"
              id="singleClosedCheck"
            />
            <label class="form-check-label fw-semibold" for="singleClosedCheck">
              Store is closed on this day
            </label>
          </div>
        </div>
        <div v-if="!selectedOperatingHour.is_closed" class="row g-3">
          <div class="col-md-6">
            <label class="form-label fw-semibold">Opening Time</label>
            <input
              type="time"
              class="form-control"
              v-model="selectedOperatingHour.opening_time"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label fw-semibold">Closing Time</label>
            <input
              type="time"
              class="form-control"
              v-model="selectedOperatingHour.closing_time"
            />
          </div>
        </div>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" @click="editOperatingHourModal = false">Cancel</button>
        <button type="button" class="btn btn-primary" @click="updateSingleOperatingHour" :disabled="savingOperatingHours">
          <span v-if="savingOperatingHours" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-check-circle me-1"></i> Update Hour
        </button>
      </div>
    </Modal>
  </div>
</template>

<script>
import storeService from "@/api/storeService";
import formatDate from "@/utils/formatDate";
import formatNumber from "@/utils/formatNumber";
import { decrypt, encrypt } from "@/utils/crypto";
import notify from "@/utils/notify";
import Badge from "@/components/badge/Badge";
import OnlineSaleNotificationEmails from "@/components/store/OnlineSaleNotificationEmails.vue";
import ImageCropper from "@/components/cropper/ImageCropper.vue";
import { CROP_PRESETS } from "@/utils/cropPresets";

export default {
  name: "StoreDetails",
  components: {
    Badge,
    OnlineSaleNotificationEmails,
    ImageCropper,
  },

  data() {
    return {
      profile: {},
      organization: {},
      currentStore: {},
      loading: true,
      store: {
        delivery_prices: [],
      },

      // Store Edit Modal
      storeModal: false,
      savingStore: false,
      editStore: {},

      // Online Store Modal
      onlineModal: false,
      onlineModalMode: "add",
      savingOnline: false,
      selectedStore: {},
      selectedFile: null,
      cropper: { open: false, file: null },
      cropPreset: CROP_PRESETS.storeBackground,

      // Delivery Prices
      deliveryPrices: [],
      deliveryPriceModal: false,
      deliveryPriceModalMode: "add",
      savingDeliveryPrice: false,
      selectedDeliveryPrice: {
        store_id: "",
        from: 0,
        to: 0,
        price: 0,
      },

      // Operating Hours
      operatingHours: [],
      addOperatingHoursModal: false,
      editOperatingHourModal: false,
      savingOperatingHours: false,
      selectedOperatingHour: {
        day_of_week: "",
        opening_time: "",
        closing_time: "",
        is_closed: false,
      },
      newOperatingHours: [],
    };
  },

  computed: {
    canManageStore() {
      return this.profile?.role && this.profile.role !== "user" && this.profile.role !== "admin";
    },
  },

  methods: {
    formatDate,
    formatNumber,

    backgroundImage(imgUrl) {
      if (imgUrl && imgUrl.trim() !== "") {
        return imgUrl;
      }
      return (process.env.VUE_APP_API_PUBLIC_URL || "") + "images/placeholder.svg";
    },

    async getStore() {
      try {
        const storeId = this.$route.params.id;
        if (!storeId) {
          notify.alert.error("Invalid store ID");
          this.$router.push("/stores");
          return;
        }

        const data = await storeService.getStore(storeId);
        if (!data) {
          notify.alert.error("Store not found");
          this.$router.push("/stores");
          return;
        }

        this.store = data;
      } catch (error) {
        console.error("Error fetching store:", error);
        notify.alert.error("Failed to load store details");
      }
    },

    // --- Store Edit ---
    openEditModal() {
      this.editStore = JSON.parse(JSON.stringify(this.store || {}));
      this.storeModal = true;
    },

    async updateStore() {
      if (!this.editStore?.name?.trim()) {
        notify.alert.error("Please enter store name");
        return;
      }

      this.savingStore = true;
      try {
        const response = await storeService.updateStore(this.editStore, this.editStore.id);
        if (response) {
          if (this.currentStore?.id === this.editStore.id) {
            sessionStorage.setItem("store", encrypt(JSON.stringify(this.editStore)));
          }
          this.storeModal = false;
          await this.getStore();
        }
      } catch (error) {
        console.error("Error updating store:", error);
        notify.alert.error("Failed to update store");
      } finally {
        this.savingStore = false;
      }
    },

    // --- Online Store ---
    openOnlineModal(mode) {
      this.onlineModalMode = mode;
      this.selectedStore = JSON.parse(JSON.stringify(this.store || {}));

      if (!this.selectedStore.online_name) {
        this.selectedStore.online_name = `${this.organization?.name || ""} - ${this.selectedStore.name || ""}`;
      }
      if (mode === "add") {
        this.selectedStore.online = 1;
      }

      this.selectedFile = null;
      this.onlineModal = true;
    },

    displayImage(event) {
      const files = event.target?.files;
      if (files && files.length) {
        this.cropper.file = files[0];
        this.cropper.open = true;
      }
      if (event.target) {
        event.target.value = "";
      }
    },

    onBackgroundCropped(dataUrl) {
      this.selectedFile = dataUrl;
      if (this.$refs.backgroundImagePreview) {
        this.$refs.backgroundImagePreview.src = dataUrl;
      }
    },

    async saveOnline() {
      if (!this.selectedStore.online_name?.trim() || !this.selectedStore.catchphrase?.trim()) {
        notify.alert.error("Please fill in online store name and catchphrase");
        return;
      }

      this.savingOnline = true;
      try {
        const storeToUpdate = JSON.parse(JSON.stringify(this.selectedStore));
        if (this.selectedFile) {
          storeToUpdate.background_image = this.selectedFile;
        }

        const response = await storeService.storeGoOnline(storeToUpdate.id, storeToUpdate, this.onlineModalMode);
        if (response) {
          this.onlineModal = false;
          await this.getStore();
        }
      } catch (error) {
        console.error("Error setting store online:", error);
        notify.alert.error("Failed to save online store details");
      } finally {
        this.savingOnline = false;
      }
    },

    // --- Delivery Prices ---
    async getDeliveryPrices() {
      const storeId = this.$route.params.id || this.store?.id;
      if (!storeId) return;
      try {
        this.deliveryPrices = await storeService.getDeliveryPrices(storeId) || [];
      } catch (error) {
        console.error("Error fetching delivery prices:", error);
      }
    },

    openDeliveryPriceModal(mode, deliveryPrice = null) {
      this.deliveryPriceModalMode = mode;
      if (mode === "edit" && deliveryPrice) {
        this.selectedDeliveryPrice = JSON.parse(JSON.stringify(deliveryPrice));
      } else {
        this.selectedDeliveryPrice = {
          store_id: this.store?.id || this.$route.params.id,
          from: 0,
          to: 0,
          price: 0,
        };
      }
      this.deliveryPriceModal = true;
    },

    async saveDeliveryPrice() {
      if (this.selectedDeliveryPrice.from >= this.selectedDeliveryPrice.to) {
        notify.alert.error("The \"From\" distance must be less than \"To\" distance");
        return;
      }
      if (this.selectedDeliveryPrice.price < 0) {
        notify.alert.error("Delivery fee cannot be negative");
        return;
      }

      this.savingDeliveryPrice = true;
      try {
        let response = false;
        if (this.deliveryPriceModalMode === "edit") {
          response = await storeService.updateDeliveryPrice(
            this.selectedDeliveryPrice,
            this.selectedDeliveryPrice.id
          );
        } else {
          this.selectedDeliveryPrice.store_id = this.store?.id || this.$route.params.id;
          response = await storeService.createDeliveryPrice(this.selectedDeliveryPrice);
        }

        if (response) {
          this.deliveryPriceModal = false;
          await this.getDeliveryPrices();
        }
      } catch (error) {
        console.error("Error saving delivery price:", error);
        notify.alert.error("Failed to save delivery price");
      } finally {
        this.savingDeliveryPrice = false;
      }
    },

    async deleteDeliveryPrice(id) {
      if (!confirm("Are you sure you want to delete this delivery price?")) return;
      try {
        const response = await storeService.deleteDeliveryPrice(id);
        if (response) {
          await this.getDeliveryPrices();
        }
      } catch (error) {
        console.error("Error deleting delivery price:", error);
        notify.alert.error("Failed to delete delivery price");
      }
    },

    // --- Operating Hours ---
    async getOperatingHours() {
      const storeId = this.$route.params.id || this.store?.id;
      if (!storeId) return;
      try {
        this.operatingHours = await storeService.getOperatingHours(storeId) || [];
      } catch (error) {
        console.error("Error fetching operating hours:", error);
      }
    },

    openAddOperatingHoursModal() {
      const defaultHours = [
        { day_of_week: "monday", opening_time: "08:00", closing_time: "20:00", is_closed: false },
        { day_of_week: "tuesday", opening_time: "08:00", closing_time: "20:00", is_closed: false },
        { day_of_week: "wednesday", opening_time: "08:00", closing_time: "20:00", is_closed: false },
        { day_of_week: "thursday", opening_time: "08:00", closing_time: "20:00", is_closed: false },
        { day_of_week: "friday", opening_time: "08:00", closing_time: "22:00", is_closed: false },
        { day_of_week: "saturday", opening_time: "10:00", closing_time: "22:00", is_closed: false },
        { day_of_week: "sunday", opening_time: "", closing_time: "", is_closed: true },
      ];

      if (this.operatingHours && this.operatingHours.length > 0) {
        this.newOperatingHours = defaultHours.map(d => {
          const existing = this.operatingHours.find(h => h.day_of_week?.toLowerCase() === d.day_of_week);
          return existing ? { ...existing, is_closed: !!existing.is_closed } : d;
        });
      } else {
        this.newOperatingHours = defaultHours;
      }

      this.addOperatingHoursModal = true;
    },

    async createOperatingHours() {
      for (const hour of this.newOperatingHours) {
        if (!hour.is_closed) {
          if (!hour.opening_time || !hour.closing_time) {
            notify.alert.error(`Please fill in times for ${hour.day_of_week}`);
            return;
          }
          if (hour.opening_time >= hour.closing_time) {
            notify.alert.error(`Opening time must be before closing time for ${hour.day_of_week}`);
            return;
          }
        }
      }

      this.savingOperatingHours = true;
      try {
        const storeId = this.store?.id || this.$route.params.id;
        const response = await storeService.createOperatingHours(storeId, {
          operating_hours: this.newOperatingHours,
        });

        if (response) {
          this.addOperatingHoursModal = false;
          await this.getOperatingHours();
        }
      } catch (error) {
        console.error("Error creating operating hours:", error);
        notify.alert.error("Failed to save operating hours");
      } finally {
        this.savingOperatingHours = false;
      }
    },

    openEditOperatingHourModal(hour) {
      this.selectedOperatingHour = JSON.parse(JSON.stringify(hour));
      this.selectedOperatingHour.is_closed = !!this.selectedOperatingHour.is_closed;
      this.editOperatingHourModal = true;
    },

    async updateSingleOperatingHour() {
      if (!this.selectedOperatingHour.is_closed) {
        if (!this.selectedOperatingHour.opening_time || !this.selectedOperatingHour.closing_time) {
          notify.alert.error("Please enter opening and closing times");
          return;
        }
        if (this.selectedOperatingHour.opening_time >= this.selectedOperatingHour.closing_time) {
          notify.alert.error("Opening time must be before closing time");
          return;
        }
      }

      this.savingOperatingHours = true;
      try {
        const storeId = this.store?.id || this.$route.params.id;
        const dataToUpdate = {
          opening_time: this.selectedOperatingHour.opening_time,
          closing_time: this.selectedOperatingHour.closing_time,
          is_closed: this.selectedOperatingHour.is_closed,
        };

        const response = await storeService.updateOperatingHour(
          storeId,
          this.selectedOperatingHour.id,
          dataToUpdate
        );

        if (response) {
          this.editOperatingHourModal = false;
          await this.getOperatingHours();
        }
      } catch (error) {
        console.error("Error updating operating hour:", error);
        notify.alert.error("Failed to update operating hour");
      } finally {
        this.savingOperatingHours = false;
      }
    },
  },

  async created() {
    try {
      const orgStr = sessionStorage.getItem("organization");
      this.organization = orgStr ? JSON.parse(decrypt(orgStr)) : {};

      const profStr = sessionStorage.getItem("profile");
      this.profile = profStr ? JSON.parse(decrypt(profStr)) : {};

      const storeStr = sessionStorage.getItem("store");
      this.currentStore = storeStr ? JSON.parse(decrypt(storeStr)) : {};

      this.loading = true;
      await this.getStore();
      await Promise.all([
        this.getDeliveryPrices(),
        this.getOperatingHours(),
      ]);
    } catch (error) {
      console.error("Error in created hook:", error);
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.store-details-content {
  padding: 0.25rem;
}

.store-title {
  font-weight: 700;
  color: #1f2937;
}

.avatar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-wrapper {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.store-avatar {
  width: 90px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  font-weight: 700;
}

.bg-primary-light {
  background-color: #eef2ff;
  color: #4361ee;
}

.fade-in {
  animation: fadeIn 0.25s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
